import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import {
  FaArrowLeft,
  FaExchangeAlt,
  FaEye,
  FaHandshake,
  FaList,
  FaPlus,
  FaPrint,
  FaSearch,
  FaSpinner,
  FaTh,
  FaTrash,
  FaUndo,
  FaUserCheck,
  FaUtensils,
} from "react-icons/fa";
import useApp from "../../hooks/useAppContext";
import { useNavigate, useParams, Link } from "react-router-dom";
import ModalReunioes from "../../components/modal/modalReunioes";
import ModalEscolhasPatrocinador from "../../components/modal/modalEscolhasPatrocinador";
import { formatarDiaHora } from "../../services/conversaoTimer";
import api from "../../services/axios";
import useAuth from "../../hooks/useAuthContext";
import { toast } from "react-toastify";
import "./admPatrocinadoresList.css";

const LABELS_TIPO = {
  reuniao_negocio: "Reunião",
  m_m: "Match & Meet",
  conteudo_patrocinado: "Conteúdo patrocinado",
  conteudo_editorial: "Conteúdo editorial",
  extraordinaria: "Especial",
};

const MENSAGEM_INFORMA_MARKETS = "Comercial Informa Markets";

const TIPOS_REUNIAO = ["reuniao_negocio", "m_m"];
const TIPOS_CONTEUDO_CONVIDADO = [
  "conteudo_patrocinado",
  "conteudo_editorial",
  "extraordinaria",
];

function normalizarHora(valor) {
  return String(valor || "").trim().substring(0, 5);
}

function dataIsoDoHorarioLabel(horario) {
  // Esperado: "05-08-2026 10:40" (dd-MM-yyyy HH:mm)
  const bruto = String(horario || "").trim();
  const m = bruto.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
}

function diaLabelDeDataIso(dataIso) {
  const m = String(dataIso || "")
    .substring(0, 10)
    .match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
}

function idUsuarioAp(ap) {
  return Number(ap?.id_usuario ?? ap?.idUsuario ?? 0);
}

function getOutrosParticipantes(agenda, userId) {
  const idNum = parseInt(userId, 10);
  return (agenda?.participantes || []).filter(
    (ap) => idUsuarioAp(ap) !== idNum
  );
}

function idSlotDaAgenda(agenda) {
  const bruto = agenda?.id_slot ?? agenda?.idSlot ?? null;
  const id = Number(bruto);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function reuniaoJaIniciada(agenda) {
  if (!agenda) return false;
  if (agenda.status === "entregue") return true;
  return (agenda.participantes || []).some((ap) => ap.presenca != null);
}

function agoraParaInputDatetimeLocal() {
  const agora = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${agora.getFullYear()}-${pad(agora.getMonth() + 1)}-${pad(
    agora.getDate()
  )}T${pad(agora.getHours())}:${pad(agora.getMinutes())}`;
}

function datetimeLocalParaIso(valor) {
  if (!valor) return null;
  const bruto = String(valor).trim();
  // datetime-local: "YYYY-MM-DDTHH:mm" — envia horário local sem converter para UTC
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(bruto)) {
    return `${bruto}:00`;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(bruto)) {
    return bruto;
  }
  return null;
}

function horarioAgendaLabel(agenda) {
  const dt = agenda?.dt_hora_inicio || agenda?.dtHoraInicio;
  if (!dt) return { horario: "—", hora: "" };
  const completo = formatarDiaHora(String(dt));
  return {
    horario: completo.substring(0, 16),
    hora: normalizarHora(completo.substring(11, 16)),
  };
}

function resolverIdSlotReuniao(reuniao, slots) {
  const agenda = reuniao?.agenda;
  if (!agenda) return null;

  const idSlot = idSlotDaAgenda(agenda) || idSlotDaAgenda(reuniao);
  if (idSlot) return idSlot;

  const horarioCompleto = formatarDiaHora(
    agenda.dt_hora_inicio || agenda.dtHoraInicio
  );
  if (!horarioCompleto) return null;

  const dia = horarioCompleto.substring(0, 10);
  const hora = normalizarHora(horarioCompleto.substring(11, 16));
  const slot = (slots || []).find((s) => {
    const diaSlot = diaLabelDeDataIso(s.data);
    return diaSlot === dia && normalizarHora(s.horario_inicio) === hora;
  });

  return slot?.id ?? null;
}

/**
 * Conta reuniões exibíveis do patrocinador por slot
 * (mesmo critério da impressão: precisa ter o outro participante).
 */
function contarReunioesPorSlot(reunioes, slots, userId) {
  const contagem = new Map();

  for (const r of reunioes || []) {
    const agenda = r?.agenda;
    if (!agenda) continue;
    if (!TIPOS_REUNIAO.includes(agenda.tipo)) continue;
    if (!getOutrosParticipantes(agenda, userId).length) continue;

    const idSlot = resolverIdSlotReuniao(r, slots);
    if (!idSlot) continue;

    contagem.set(idSlot, (contagem.get(idSlot) || 0) + 1);
  }

  return contagem;
}

function montarItemInformaMarkets(slot) {
  const dia = diaLabelDeDataIso(slot.data);
  const hora = normalizarHora(slot.horario_inicio);

  return {
    key: `informa-markets-${slot.id}`,
    idAgenda: null,
    idSlot: slot.id,
    tipo: "reuniao_negocio",
    horario: `${dia} ${hora}`,
    hora,
    titulo: MENSAGEM_INFORMA_MARKETS,
    empresa: "",
    kind: "informa",
    isInforma: true,
    isMm: false,
    isExtra: false,
    isAlmoco: false,
    isExtraordinaria: false,
    iniciada: false,
    podeAcoesAdmin: false,
    podeCheckin: false,
  };
}

/**
 * Escolhe o slot para a reunião Informa Markets.
 * Advance (cota simultânea ≥ 2): prioriza o primeiro slot com exatamente
 * 1 reunião (ainda cabe a segunda). Senão, usa o primeiro slot com vaga.
 */
function escolherSlotInformaMarkets(slots, reunioes, userId, cotaSimultania) {
  if (!slots?.length) return null;

  const cota = Math.max(1, Number(cotaSimultania) || 1);
  const contagem = contarReunioesPorSlot(reunioes, slots, userId);

  if (cota >= 2) {
    const comUmaReuniao = slots.find((s) => (contagem.get(s.id) || 0) === 1);
    if (comUmaReuniao) return comUmaReuniao;
  }

  return slots.find((s) => (contagem.get(s.id) || 0) < cota) || null;
}

/**
 * Em agendas de patrocinador, reserva um slot com vaga para
 * a reunião institucional com a Informa Markets (igual à impressão).
 */
function inserirReuniaoInformaMarkets(
  itens,
  reunioes,
  slots,
  userId,
  cotaSimultania
) {
  const slotEscolhido = escolherSlotInformaMarkets(
    slots,
    reunioes,
    userId,
    cotaSimultania
  );
  if (!slotEscolhido) return itens;

  const novos = [...(itens || []), montarItemInformaMarkets(slotEscolhido)];
  novos.sort((a, b) =>
    String(a.horario || "").localeCompare(String(b.horario || ""))
  );
  return novos;
}

function montarItensAgenda(
  reunioes,
  userId,
  isPatrocinador,
  slots = [],
  cotaSimultania = 1
) {
  const itens = [];

  for (const r of reunioes || []) {
    const agenda = r?.agenda;
    if (!agenda?.tipo) continue;

    const { horario, hora } = horarioAgendaLabel(agenda);

    if (TIPOS_REUNIAO.includes(agenda.tipo)) {
      const outros = getOutrosParticipantes(agenda, userId);
      // Na agenda do patrocinador, só lista reuniões com o outro participante
      if (isPatrocinador && !outros.length) continue;

      const outroUsuario = outros[0]?.usuario;
      const empresaOutro =
        outroUsuario?.empresa && outroUsuario.empresa !== "null"
          ? outroUsuario.empresa
          : "";

      itens.push({
        key: r.id,
        idAgenda: agenda.id,
        idSlot: idSlotDaAgenda(agenda) || idSlotDaAgenda(r),
        tipo: agenda.tipo,
        horario,
        hora,
        titulo: (outroUsuario?.nome || agenda.nome || "—").toUpperCase(),
        empresa: empresaOutro,
        kind: "reuniao",
        isInforma: false,
        isMm: agenda.tipo === "m_m",
        isExtra: agenda.nome === "Reunião de negócios*",
        isAlmoco: agenda.nome === "Reunião de almoço/jantar",
        isExtraordinaria: false,
        iniciada: reuniaoJaIniciada(agenda),
        podeAcoesAdmin: Boolean(isPatrocinador),
        podeCheckin: true,
      });
      continue;
    }

    // Agenda do convidado: conteúdos editoriais/patrocinados e extraordinárias
    if (!isPatrocinador && TIPOS_CONTEUDO_CONVIDADO.includes(agenda.tipo)) {
      itens.push({
        key: r.id,
        idAgenda: agenda.id,
        idSlot: idSlotDaAgenda(agenda) || idSlotDaAgenda(r),
        tipo: agenda.tipo,
        horario,
        hora,
        titulo: (agenda.nome || "—").toUpperCase(),
        empresa: "",
        kind: agenda.tipo === "extraordinaria" ? "extraordinaria" : "conteudo",
        isInforma: false,
        isMm: false,
        isExtra: false,
        isAlmoco: false,
        isExtraordinaria: agenda.tipo === "extraordinaria",
        iniciada: false,
        podeAcoesAdmin: false,
        podeCheckin: false,
      });
    }
  }

  if (isPatrocinador) {
    return inserirReuniaoInformaMarkets(
      itens,
      reunioes,
      slots,
      userId,
      cotaSimultania
    );
  }

  return itens;
}

function tipoPrincipalItens(itens) {
  if (!itens.length) return null;
  if (itens.every((i) => i.isMm || i.tipo === "m_m")) return "m_m";
  if (
    itens.some(
      (i) => i.isInforma || (i.kind === "reuniao" && !i.isMm)
    )
  ) {
    return "reuniao";
  }
  if (itens.some((i) => i.kind === "conteudo")) return "conteudo";
  if (itens.some((i) => i.kind === "extraordinaria" || i.isExtraordinaria)) {
    return "extraordinaria";
  }
  return "outro";
}

function tipoLabelItem(item) {
  if (!item) return "Disponível";
  if (item.isInforma) return "Reunião";
  if (item.isMm || item.tipo === "m_m") return "Match & Meet";
  if (item.isExtraordinaria || item.tipo === "extraordinaria") {
    return "Especial";
  }
  if (item.kind === "conteudo") return "Conteúdo";
  if (item.isAlmoco) return "Jantar";
  return LABELS_TIPO[item.tipo] || "Reunião";
}

const AdmReunioesConvidados = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { usuario } = useAuth();
  const { excluirAgenda } = useApp();

  const [convidado, setConvidado] = useState(null);
  const [reunioes, setReunioes] = useState([]);
  const [slots, setSlots] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalTipoAgenda, setModalTipoAgenda] = useState("reuniao_negocio");
  const [showModalEscolhas, setShowModalEscolhas] = useState(false);
  const [showContador, setShowContador] = useState(false);
  const [quantidadeMarcada, setQuantidadeMarcada] = useState(0);
  const [idsConvidados, setIdsConvidados] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [modoVisualizacao, setModoVisualizacao] = useState("lista");
  const [checkinItem, setCheckinItem] = useState(null);
  const [dtCheckin, setDtCheckin] = useState("");
  const [salvandoCheckin, setSalvandoCheckin] = useState(false);
  const [resultadoSubstituicao, setResultadoSubstituicao] = useState(null);
  const [substituindo, setSubstituindo] = useState(false);

  const isAdmin = usuario?.tipo === "administracao";

  const carregaUsuario = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      const [resp, respSlots] = await Promise.all([
        api.get(`/usuarios-get/${id}`),
        api.get("/slots").catch(() => null),
      ]);
      const dados = resp.data.user;
      setConvidado(dados);

      if (Array.isArray(respSlots?.data?.dados)) {
        setSlots(respSlots.data.dados);
      } else {
        setSlots([]);
      }

      if (dados.plano) {
        const respPatrocinador = await api.post(
          "/patrocinador-verifica-disponibilidade",
          { id_info: dados.infoPatrocinador.id }
        );

        const patro = respPatrocinador.data.patrocinador;
        const arrayIds = [];

        (patro.usuarios || []).forEach((u) => {
          (u.agendaParticipante || []).forEach((ap) => {
            if (
              (ap.agenda?.tipo === "reuniao_negocio" ||
                ap.agenda?.tipo === "m_m") &&
              ap.agenda.nome !== "Reunião de almoço/jantar" &&
              ap.agenda.nome !== "Reunião de negócios*"
            ) {
              arrayIds.push(ap.id_agenda);
            }
          });
        });

        const idsMarcados = (dados.agendaParticipante || [])
          .map((ap) => {
            if (
              (ap.agenda?.tipo === "reuniao_negocio" ||
                ap.agenda?.tipo === "m_m") &&
              ap.agenda.nome !== "Reunião de almoço/jantar" &&
              ap.agenda.nome !== "Reunião de negócios*"
            ) {
              return ap.agenda.participantes?.[0]?.id_usuario;
            }
            return null;
          })
          .filter(Boolean);

        setIdsConvidados(idsMarcados);
        setQuantidadeMarcada([...new Set(arrayIds)].length);
        setShowContador(true);
      } else {
        setShowContador(false);
        setQuantidadeMarcada(0);
        setIdsConvidados([]);
      }

      const ordenacao = [...(dados.agendaParticipante || [])]
        .filter((ap) => ap?.agenda)
        .sort((a, b) => {
          const dataA = new Date(
            a.agenda.dt_hora_inicio || a.agenda.dtHoraInicio
          );
          const dataB = new Date(
            b.agenda.dt_hora_inicio || b.agenda.dtHoraInicio
          );
          return dataA - dataB;
        });

      setReunioes(ordenacao);
    } catch (err) {
      console.error("Erro ao carregar agenda:", err);
      setError("Erro ao carregar a agenda do usuário");
      setConvidado(null);
      setReunioes([]);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    carregaUsuario();
  }, [carregaUsuario]);

  const handleModal = (tipoAgenda = "reuniao_negocio") => {
    if (showModal) {
      setShowModal(false);
      carregaUsuario();
      return;
    }
    setModalTipoAgenda(tipoAgenda);
    carregaUsuario();
    setShowModal(true);
  };

  const handleExclusao = async (idAgenda) => {
    const confirmacao = window.confirm(
      "Tem certeza que deseja excluir esta reunião? Esta ação não pode ser desfeita."
    );
    if (!confirmacao) return;

    await excluirAgenda(idAgenda);
    carregaUsuario();
  };

  const handleSubstituir = async (item) => {
    const idAgenda = item?.idAgenda;
    if (!idAgenda) return;

    const confirmacao = window.confirm(
      "Tem certeza que deseja substituir esta reunião? Esta ação não pode ser desfeita."
    );
    if (!confirmacao) return;

    setSubstituindo(true);
    try {
      const resp = await api.post("/agenda-substituir", { id_agenda: idAgenda });
      if (resp.status === 200) {
        if (resp.data.participanteSubstituto?.substitutoEncontrado) {
          const sub = resp.data.substituicao || {};
          setResultadoSubstituicao({
            idSaiu: sub.convidadoSaiu?.id || null,
            convidadoSaiu:
              sub.convidadoSaiu?.nome || item.titulo || "Convidado anterior",
            idEntrou:
              sub.convidadoEntrou?.id ||
              resp.data.participanteSubstituto?.participante?.id ||
              null,
            convidadoEntrou:
              sub.convidadoEntrou?.nome ||
              resp.data.participanteSubstituto?.participante?.nome ||
              "Novo convidado",
            listaEntrou:
              sub.convidadoEntrou?.lista ||
              (resp.data.participanteSubstituto?.prioridade === "mais_quero"
                ? "lista prioritária"
                : resp.data.participanteSubstituto?.prioridade === "quero"
                  ? "lista desejável"
                  : null),
            sequenciaEntrou:
              sub.convidadoEntrou?.sequencia ??
              resp.data.participanteSubstituto?.sequencia ??
              null,
            tipoEscolhaEntrou:
              sub.convidadoEntrou?.tipoEscolha ||
              resp.data.participanteSubstituto?.prioridade ||
              null,
            slotAnterior: sub.slotAnterior || null,
            slotNovo: sub.slotNovo || {
              id: resp.data.participanteSubstituto?.idSlot,
              data: resp.data.participanteSubstituto?.diaRecomendado,
              hora: resp.data.participanteSubstituto?.slotRecomendado,
            },
            slotAlterado: Boolean(sub.slotAlterado),
            idAgendaNova: resp.data.id_agenda_nova,
          });
          carregaUsuario();
        } else {
          const motivo =
            resp.data.participanteSubstituto?.motivoIndisponibilidade ||
            "Motivo não informado";
          toast.error(
            `Não foi possível encontrar um substituto para a reunião! ${motivo}`
          );
        }
      }
    } catch (err) {
      console.error("Erro ao substituir reunião:", err);
      toast.error(
        err?.response?.data?.mensagem ||
          "Erro ao substituir a reunião. Tente novamente."
      );
    } finally {
      setSubstituindo(false);
    }
  };

  const fecharModalSubstituicao = () => {
    setResultadoSubstituicao(null);
  };

  const formatarSlotSubstituicao = (slot) => {
    if (!slot) return "—";
    const partes = [];
    if (slot.id != null) partes.push(`Slot #${slot.id}`);
    if (slot.data || slot.hora) {
      partes.push([slot.data, slot.hora].filter(Boolean).join(" "));
    }
    return partes.join(" · ") || "—";
  };

  const handleModalEscolhas = () => {
    setShowModalEscolhas((prev) => !prev);
  };

  const abrirModalCheckin = (item) => {
    setCheckinItem(item);
    setDtCheckin(agoraParaInputDatetimeLocal());
  };

  const fecharModalCheckin = () => {
    if (salvandoCheckin) return;
    setCheckinItem(null);
    setDtCheckin("");
  };

  const handleConfirmarCheckin = async () => {
    if (!checkinItem?.idAgenda) return;

    const confirmar = window.confirm(
      `Confirmar check-in da reunião com ${checkinItem.titulo}?`
    );
    if (!confirmar) return;

    const iso = datetimeLocalParaIso(dtCheckin);
    if (!iso) {
      toast.error("Informe uma data e hora válidas para o check-in.");
      return;
    }

    setSalvandoCheckin(true);
    try {
      const resp = await api.post("/agenda-checkin-adm", {
        id_agenda: checkinItem.idAgenda,
        dt_presenca: iso,
      });
      if (resp.status === 200) {
        toast.success(resp.data?.mensagem || "Check-in realizado com sucesso!");
        setCheckinItem(null);
        setDtCheckin("");
        carregaUsuario();
      }
    } catch (err) {
      console.error("Erro ao fazer check-in:", err);
      toast.error(
        err?.response?.data?.mensagem || "Erro ao realizar o check-in."
      );
    } finally {
      setSalvandoCheckin(false);
    }
  };

  const handleReverterCheckin = async (item) => {
    const confirmar = window.confirm(
      `Reverter o check-in da reunião com ${item.titulo}? A reunião voltará ao estado de não iniciada.`
    );
    if (!confirmar) return;

    try {
      const resp = await api.post("/agenda-reverter-checkin-adm", {
        id_agenda: item.idAgenda,
      });
      if (resp.status === 200) {
        toast.success(resp.data?.mensagem || "Check-in revertido com sucesso!");
        carregaUsuario();
      }
    } catch (err) {
      console.error("Erro ao reverter check-in:", err);
      toast.error(
        err?.response?.data?.mensagem || "Erro ao reverter o check-in."
      );
    }
  };

  const isAgendaPatrocinador = Boolean(
    showContador || convidado?.infoPatrocinador?.id
  );

  const cotaSimultania =
    convidado?.plano?.cota_simultania ??
    convidado?.plano?.cotaSimultania ??
    1;

  const itensAgenda = useMemo(
    () =>
      montarItensAgenda(
        reunioes,
        id,
        isAgendaPatrocinador,
        slots,
        cotaSimultania
      ),
    [reunioes, id, isAgendaPatrocinador, slots, cotaSimultania]
  );

  const itensFiltrados = useMemo(() => {
    if (!filtro.trim()) return itensAgenda;

    const termo = filtro.toLowerCase().trim();
    return itensAgenda.filter((item) => {
      const campos = [
        item.horario,
        item.titulo,
        item.empresa,
        item.tipo,
        LABELS_TIPO[item.tipo],
        item.isInforma ? "informa markets comercial" : "",
        item.isMm ? "m&m match meet" : "",
        item.isExtra ? "reunião extra" : "",
        item.isAlmoco ? "almoço jantar" : "",
        item.isExtraordinaria ? "especial extraordinária extraordinaria" : "",
        item.kind === "conteudo" ? "conteúdo conteudo" : "",
      ];
      return campos.some((campo) =>
        String(campo || "")
          .toLowerCase()
          .includes(termo)
      );
    });
  }, [itensAgenda, filtro]);

  const slotsAgenda = useMemo(() => {
    const porSlot = new Map();
    const semSlot = [];

    for (const item of itensFiltrados) {
      if (item.idSlot) {
        const lista = porSlot.get(item.idSlot) || [];
        lista.push(item);
        porSlot.set(item.idSlot, lista);
      } else {
        semSlot.push(item);
      }
    }

    // Fallback por data+horário quando id_slot não veio na agenda
    if (semSlot.length && slots.length) {
      for (const item of semSlot) {
        const horaItem = item.hora;
        const dataItem = dataIsoDoHorarioLabel(item.horario);
        const slot =
          slots.find((s) => {
            const dataSlot =
              String(s.data || "").substring(0, 10) ||
              String(s.data_iso || "").substring(0, 10);
            return (
              normalizarHora(s.horario_inicio) === horaItem &&
              (!dataItem || !dataSlot || dataSlot === dataItem)
            );
          }) || null;

        if (slot) {
          const lista = porSlot.get(slot.id) || [];
          lista.push(item);
          porSlot.set(slot.id, lista);
        }
      }
    }

    if (slots.length) {
      return slots.map((slot) => {
        const itens = porSlot.get(slot.id) || [];
        return {
          id: slot.id,
          hora: normalizarHora(slot.horario_inicio),
          itens,
        };
      });
    }

    // Sem cadastro de slots: monta grid a partir dos itens
    const porHora = new Map();
    for (const item of itensFiltrados) {
      const chave = normalizarHora(item.hora) || item.horario || String(item.key);
      const lista = porHora.get(chave) || [];
      lista.push(item);
      porHora.set(chave, lista);
    }

    return [...porHora.entries()].map(([hora, itens], index) => ({
      id: index + 1,
      hora: normalizarHora(hora),
      itens,
    }));
  }, [itensFiltrados, slots]);

  const slotsOcupados = useMemo(
    () => slotsAgenda.filter((s) => s.itens.length > 0).length,
    [slotsAgenda]
  );

  const indicadores = useMemo(() => {
    const reunioesCount = itensAgenda.filter((i) => i.kind === "reuniao").length;
    const conteudosCount = itensAgenda.filter(
      (i) => i.kind === "conteudo"
    ).length;

    return {
      total: itensAgenda.length,
      reunioesCount,
      conteudosCount,
      cotaReunioes: convidado?.plano?.cota_reunioes ?? 0,
      cotaSimultaneas: convidado?.plano?.cota_simultania ?? 0,
      quantidadeMarcada,
    };
  }, [itensAgenda, convidado, quantidadeMarcada]);

  const podeAdicionar =
    isAdmin &&
    showContador &&
    quantidadeMarcada < (convidado?.plano?.cota_reunioes ?? 0);

  const colunaPrincipal = showContador ? "Convidado" : "Participante / Evento";

  const renderAcoesItem = (item) => {
    const podeGestao = isAdmin && item.podeAcoesAdmin;
    const podeCheckin = isAdmin && item.podeCheckin;

    if (!podeGestao && !podeCheckin) {
      return <span className="apl-stat-muted">—</span>;
    }

    return (
      <>
        {podeCheckin && !item.iniciada ? (
          <button
            type="button"
            className="apl-action-btn"
            onClick={() => abrirModalCheckin(item)}
            title="Fazer check-in da reunião"
          >
            <FaUserCheck size={12} aria-hidden />
            Check-in
          </button>
        ) : null}
        {podeCheckin && item.iniciada ? (
          <button
            type="button"
            className="apl-action-btn"
            onClick={() => handleReverterCheckin(item)}
            title="Reverter check-in da reunião"
          >
            <FaUndo size={12} aria-hidden />
            Reverter check-in
          </button>
        ) : null}
        {podeGestao && !item.isMm ? (
          <button
            type="button"
            className="apl-action-btn"
            onClick={() => handleSubstituir(item)}
            title="Substituir reunião"
            disabled={substituindo}
          >
            <FaExchangeAlt size={12} aria-hidden />
            Substituir
          </button>
        ) : null}
        {podeGestao ? (
          <button
            type="button"
            className="apl-action-btn apl-action-btn--danger"
            onClick={() => handleExclusao(item.idAgenda)}
            title="Excluir reunião"
          >
            <FaTrash size={12} aria-hidden />
            Excluir
          </button>
        ) : null}
      </>
    );
  };

  return (
    <div className="container-adm-main">
      <ModalReunioes
        titulo={
          modalTipoAgenda === "m_m"
            ? "Nova reunião Match & Meet"
            : "Nova Reunião de trabalho"
        }
        tipoAgenda={modalTipoAgenda}
        to="/agenda"
        labelTo=""
        show={showModal}
        toggleModal={() => handleModal(modalTipoAgenda)}
        id={id}
        cota={convidado?.plano?.cota_simultania}
        cota_quantidade={convidado?.plano?.cota_reunioes}
        id_info={convidado?.infoPatrocinador?.id}
        ids_convidados={idsConvidados}
        carrega_usuario={() => carregaUsuario()}
      />
      <ModalEscolhasPatrocinador
        show={showModalEscolhas}
        toggleModal={handleModalEscolhas}
        titulo="Escolhas do Patrocinador"
        userId={id}
      />

      <Modal
        show={Boolean(checkinItem)}
        onHide={fecharModalCheckin}
        centered
        size="md"
        backdrop={salvandoCheckin ? "static" : true}
        className="arc-checkin-modal"
        dialogClassName="arc-checkin-modal-dialog"
      >
        <Modal.Header closeButton={!salvandoCheckin}>
          <Modal.Title>Check-in da reunião</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="arc-checkin-modal-texto">
            Informe a data e hora do check-in para{" "}
            <strong>{checkinItem?.titulo || "a reunião"}</strong>. O registro
            seguirá o mesmo fluxo da recepção (presença + status entregue) e
            gerará log na agenda.
          </p>
          <div className="arc-checkin-modal-field">
            <label htmlFor="arc-dt-checkin">Data e hora do check-in</label>
            <input
              id="arc-dt-checkin"
              type="datetime-local"
              className="apl-search-input arc-checkin-modal-input"
              value={dtCheckin}
              onChange={(e) => setDtCheckin(e.target.value)}
              disabled={salvandoCheckin}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="arc-checkin-modal-footer">
          <button
            type="button"
            className="apl-btn apl-btn--ghost"
            onClick={fecharModalCheckin}
            disabled={salvandoCheckin}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="apl-btn apl-btn--primary"
            onClick={handleConfirmarCheckin}
            disabled={salvandoCheckin || !dtCheckin}
          >
            {salvandoCheckin ? (
              <>
                <FaSpinner className="fa-spin" size={14} aria-hidden />
                Salvando…
              </>
            ) : (
              <>
                <FaUserCheck size={14} aria-hidden />
                Confirmar check-in
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(resultadoSubstituicao)}
        onHide={fecharModalSubstituicao}
        centered
        size="md"
        className="arc-substituicao-modal"
        dialogClassName="arc-substituicao-modal-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>Substituição realizada</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="arc-substituicao-modal-texto">
            A reunião foi substituída com sucesso. Confira o resumo da
            movimentação:
          </p>
          <dl className="arc-substituicao-resumo">
            <div className="arc-substituicao-resumo__item">
              <dt>Saiu</dt>
              <dd>
                <span>{resultadoSubstituicao?.convidadoSaiu || "—"}</span>
                {resultadoSubstituicao?.idSaiu ? (
                  <Link
                    className="arc-substituicao-link"
                    to={`/private/adm/convidados/reunioes/${resultadoSubstituicao.idSaiu}`}
                    onClick={fecharModalSubstituicao}
                  >
                    Ver agenda
                  </Link>
                ) : null}
              </dd>
            </div>
            <div className="arc-substituicao-resumo__item">
              <dt>Entrou</dt>
              <dd>
                <div className="arc-substituicao-pessoa">
                  <span>{resultadoSubstituicao?.convidadoEntrou || "—"}</span>
                  {resultadoSubstituicao?.listaEntrou ||
                  resultadoSubstituicao?.sequenciaEntrou != null ? (
                    <span className="arc-substituicao-escolha">
                      {resultadoSubstituicao?.sequenciaEntrou != null
                        ? `${resultadoSubstituicao.sequenciaEntrou}º`
                        : null}
                      {resultadoSubstituicao?.sequenciaEntrou != null &&
                      resultadoSubstituicao?.listaEntrou
                        ? " da "
                        : null}
                      {resultadoSubstituicao?.listaEntrou || null}
                    </span>
                  ) : null}
                </div>
                {resultadoSubstituicao?.idEntrou ? (
                  <Link
                    className="arc-substituicao-link"
                    to={`/private/adm/convidados/reunioes/${resultadoSubstituicao.idEntrou}`}
                    onClick={fecharModalSubstituicao}
                  >
                    Ver agenda
                  </Link>
                ) : null}
              </dd>
            </div>
            <div className="arc-substituicao-resumo__item">
              <dt>Slot anterior</dt>
              <dd>
                {formatarSlotSubstituicao(resultadoSubstituicao?.slotAnterior)}
              </dd>
            </div>
            <div className="arc-substituicao-resumo__item">
              <dt>Slot novo</dt>
              <dd>
                {formatarSlotSubstituicao(resultadoSubstituicao?.slotNovo)}
                {resultadoSubstituicao?.slotAlterado ? (
                  <span className="arc-substituicao-badge">Horário alterado</span>
                ) : (
                  <span className="arc-substituicao-badge arc-substituicao-badge--mesmo">
                    Mesmo horário
                  </span>
                )}
              </dd>
            </div>
          </dl>
        </Modal.Body>
        <Modal.Footer className="arc-substituicao-modal-footer">
          <button
            type="button"
            className="apl-btn apl-btn--primary"
            onClick={fecharModalSubstituicao}
          >
            Fechar
          </button>
        </Modal.Footer>
      </Modal>

      <div className="apl-page">
        <header className="apl-header" aria-label="Cabeçalho da listagem">
          <div className="apl-header-text">
            <h1>Agenda{convidado?.nome ? ` — ${convidado.nome}` : ""}</h1>
            <p>
              Visualize horários, participantes e conteúdos vinculados a este
              usuário. Gerencie reuniões quando houver cota disponível.
            </p>
          </div>

          <div className="apl-toolbar">
            <div className="apl-search">
              <span className="apl-search-icon" aria-hidden>
                <FaSearch size={14} />
              </span>
              <input
                type="search"
                className="apl-search-input"
                placeholder="Buscar por horário, nome ou tipo…"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                disabled={isLoading}
                aria-label="Buscar item da agenda"
              />
            </div>

            <div
              className="arc-view-toggle"
              role="group"
              aria-label="Modo de visualização"
            >
              <button
                type="button"
                className={`apl-btn apl-btn--ghost arc-view-btn${
                  modoVisualizacao === "lista" ? " is-active" : ""
                }`}
                onClick={() => setModoVisualizacao("lista")}
                title="Visualizar como lista"
                aria-pressed={modoVisualizacao === "lista"}
              >
                <FaList size={14} aria-hidden />
                Lista
              </button>
              <button
                type="button"
                className={`apl-btn apl-btn--ghost arc-view-btn${
                  modoVisualizacao === "slots" ? " is-active" : ""
                }`}
                onClick={() => setModoVisualizacao("slots")}
                title="Visualizar por slots"
                aria-pressed={modoVisualizacao === "slots"}
              >
                <FaTh size={14} aria-hidden />
                Slots
              </button>
            </div>

            {showContador && isAdmin && convidado?.infoPatrocinador?.id ? (
              <button
                type="button"
                className="apl-btn apl-btn--secondary"
                onClick={() =>
                  navigate(
                    `/private/adm/reuniao-jantar/${convidado.infoPatrocinador.id}`
                  )
                }
              >
                <FaUtensils size={14} aria-hidden />
                Almoço/Jantar
              </button>
            ) : null}

            {showContador && isAdmin && convidado?.infoPatrocinador?.id ? (
              <button
                type="button"
                className="apl-btn apl-btn--secondary"
                onClick={() => handleModal("m_m")}
                title="Incluir reunião Match & Meet"
              >
                <FaHandshake size={14} aria-hidden />
                M&M
              </button>
            ) : null}

            {convidado?.infoPatrocinador ? (
              <button
                type="button"
                className="apl-btn apl-btn--secondary"
                onClick={handleModalEscolhas}
                title="Ver escolhas do patrocinador"
              >
                <FaEye size={14} aria-hidden />
                Escolhas
              </button>
            ) : null}

            <button
              type="button"
              className="apl-btn apl-btn--secondary"
              onClick={() => navigate(`/private/adm/convidados/imprimir/${id}`)}
              disabled={!id}
            >
              <FaPrint size={14} aria-hidden />
              Imprimir
            </button>

            {podeAdicionar ? (
              <button
                type="button"
                className="apl-btn apl-btn--primary"
                onClick={() => handleModal("reuniao_negocio")}
              >
                <FaPlus size={14} aria-hidden />
                Adicionar
              </button>
            ) : null}

            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={() => navigate(-1)}
              title="Voltar"
            >
              <FaArrowLeft size={14} aria-hidden />
              Voltar
            </button>
          </div>
        </header>

        <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
          {error ? (
            <div className="apl-alert" role="alert">
              {error}
            </div>
          ) : null}

          {!isLoading && convidado ? (
            <section
              className="apl-metrics"
              aria-label="Indicadores da agenda"
            >
              {showContador ? (
                <>
                  <div className="apl-metric apl-metric--total">
                    <span className="apl-metric-value">
                      {indicadores.quantidadeMarcada}
                      <span className="apl-stat-muted" style={{ fontSize: "1rem" }}>
                        {" "}
                        / {indicadores.cotaReunioes}
                      </span>
                    </span>
                    <span className="apl-metric-label">Reuniões agendadas</span>
                  </div>
                  <div className="apl-metric">
                    <span className="apl-metric-value">
                      {indicadores.cotaSimultaneas}
                    </span>
                    <span className="apl-metric-label">Simultâneas</span>
                  </div>
                  <div className="apl-metric">
                    <span className="apl-metric-value">{indicadores.total}</span>
                    <span className="apl-metric-label">
                      {indicadores.total === 1 ? "Item na agenda" : "Itens na agenda"}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="apl-metric apl-metric--total">
                    <span className="apl-metric-value">{indicadores.total}</span>
                    <span className="apl-metric-label">
                      {indicadores.total === 1 ? "Item na agenda" : "Itens na agenda"}
                    </span>
                  </div>
                  <div className="apl-metric">
                    <span className="apl-metric-value">
                      {indicadores.reunioesCount}
                    </span>
                    <span className="apl-metric-label">Reuniões</span>
                  </div>
                  <div className="apl-metric">
                    <span className="apl-metric-value">
                      {indicadores.conteudosCount}
                    </span>
                    <span className="apl-metric-label">Conteúdos</span>
                  </div>
                </>
              )}
            </section>
          ) : null}

          {isLoading ? (
            <div className="apl-card">
              <div className="apl-loading">
                <FaSpinner className="fa-spin" size={28} aria-hidden />
                <span>Carregando agenda…</span>
              </div>
            </div>
          ) : modoVisualizacao === "slots" ? (
            <section className="apl-card" aria-label="Agenda por slots">
              {slotsAgenda.length === 0 ? (
                <div className="apl-empty">
                  <p className="apl-empty-title">Nenhum slot disponível</p>
                  <p style={{ margin: 0 }}>
                    Não há horários cadastrados para montar a visão por slots.
                  </p>
                </div>
              ) : (
                <div className="arc-slots">
                  <div className="arc-slots-head">
                    <h3>
                      {showContador
                        ? "Agenda do patrocinador"
                        : "Agenda do preview"}
                    </h3>
                    <span className="arc-slots-hint">
                      {slotsOcupados} de {slotsAgenda.length} slots ocupados
                    </span>
                  </div>
                  <div className="arc-slots-grid">
                    {slotsAgenda.map((slot) => {
                      const itens = slot.itens || [];
                      const tipo = tipoPrincipalItens(itens);
                      return (
                        <article
                          key={slot.id}
                          className={`arc-slot-card${
                            itens.length > 0
                              ? ` is-${tipo || "outro"}`
                              : " is-livre"
                          }`}
                        >
                          <header>
                            <span className="arc-slot-id">Slot {slot.id}</span>
                            {slot.hora ? (
                              <span className="arc-slot-hora">{slot.hora}</span>
                            ) : null}
                          </header>
                          {itens.length > 1 ? (
                            <ul className="arc-slot-labels">
                              {itens.map((item) => (
                                <li key={item.key}>
                                  {item.titulo}
                                  {item.empresa ? (
                                    <span className="apl-stat-muted">
                                      {" "}
                                      · {item.empresa}
                                    </span>
                                  ) : null}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <>
                              <p className="arc-slot-label">
                                {itens[0]?.titulo || "Livre"}
                              </p>
                              {itens[0]?.empresa ? (
                                <p
                                  className="apl-stat-muted"
                                  style={{ margin: "0.15rem 0 0" }}
                                >
                                  {itens[0].empresa}
                                </p>
                              ) : null}
                            </>
                          )}
                          <span className="arc-slot-tipo">
                            {itens.length === 0
                              ? "Disponível"
                              : itens.length > 1
                                ? `${itens.length} marcações`
                                : tipoLabelItem(itens[0])}
                          </span>
                          {itens.some(
                            (i) =>
                              isAdmin && (i.podeAcoesAdmin || i.podeCheckin)
                          ) ? (
                            <div className="arc-slot-actions">
                              {itens.map((item) =>
                                isAdmin &&
                                (item.podeAcoesAdmin || item.podeCheckin) ? (
                                  <div
                                    key={`acoes-${item.key}`}
                                    className="apl-actions"
                                  >
                                    {renderAcoesItem(item)}
                                  </div>
                                ) : null
                              )}
                            </div>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                </div>
              )}

              {itensFiltrados.length > 0 || slotsAgenda.length > 0 ? (
                <footer className="apl-footer">
                  <span>
                    {slotsOcupados}{" "}
                    {slotsOcupados === 1 ? "slot ocupado" : "slots ocupados"}
                    {filtro ? (
                      <>
                        {" "}
                        <span className="apl-stat-muted">
                          (filtro ativo · {itensFiltrados.length}{" "}
                          {itensFiltrados.length === 1 ? "item" : "itens"})
                        </span>
                      </>
                    ) : null}
                  </span>
                  <span>
                    {filtro
                      ? "Busca ativa"
                      : showContador
                        ? "Agenda do patrocinador"
                        : "Agenda do convidado"}
                  </span>
                </footer>
              ) : null}
            </section>
          ) : (
            <section className="apl-card" aria-label="Tabela da agenda">
              <div className="apl-table-wrap">
                {itensFiltrados.length === 0 ? (
                  <div className="apl-empty">
                    <p className="apl-empty-title">
                      {itensAgenda.length === 0
                        ? isAgendaPatrocinador
                          ? "Nenhuma reunião agendada"
                          : "Nenhum item na agenda"
                        : "Nenhum resultado"}
                    </p>
                    <p style={{ margin: 0 }}>
                      {itensAgenda.length === 0
                        ? podeAdicionar
                          ? "Use “Adicionar” para incluir uma nova reunião."
                          : isAgendaPatrocinador
                            ? "Não há reuniões para exibir nesta agenda."
                            : "Não há reuniões, M&M, conteúdos ou agendas extraordinárias para este convidado."
                        : "Ajuste a busca para ver itens da agenda."}
                    </p>
                  </div>
                ) : (
                  <table className="apl-table">
                    <thead>
                      <tr>
                        <th scope="col">Horário</th>
                        <th scope="col">{colunaPrincipal}</th>
                        {showContador ? <th scope="col">Empresa</th> : null}
                        <th scope="col">Tipo</th>
                        <th scope="col" style={{ textAlign: "right" }}>
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {itensFiltrados.map((item) => (
                        <tr key={item.key}>
                          <td>
                            <span
                              className={`apl-stat${
                                item.isMm ? " apl-horario--mm" : ""
                              }`}
                            >
                              {item.horario}
                            </span>
                          </td>
                          <td className="apl-cell-name">
                            <div className="apl-cell-stack">
                              <span>{item.titulo}</span>
                              {!showContador && item.empresa ? (
                                <span className="apl-stat-muted">
                                  {item.empresa}
                                </span>
                              ) : null}
                              {item.iniciada ? (
                                <span className="apl-tag-type">
                                  Check-in feito
                                </span>
                              ) : null}
                              {item.isExtra ? (
                                <span className="apl-tag-type apl-tag-type--warn">
                                  Reunião extra
                                </span>
                              ) : null}
                              {item.isAlmoco ? (
                                <span className="apl-tag-type apl-tag-type--warn">
                                  Jantar
                                </span>
                              ) : null}
                              {item.isExtraordinaria ? (
                                <span className="apl-tag-type apl-tag-type--especial">
                                  Especial
                                </span>
                              ) : null}
                              {item.kind === "conteudo" ? (
                                <span className="apl-tag-type">
                                  Conteúdo
                                </span>
                              ) : null}
                            </div>
                          </td>
                          {showContador ? (
                            <td>
                              {item.empresa ? (
                                item.empresa
                              ) : (
                                <span className="apl-stat-muted">—</span>
                              )}
                            </td>
                          ) : null}
                          <td>
                            {item.tipo ? (
                              <span
                                className={`apl-tag-type${
                                  item.isMm
                                    ? " apl-tag-type--mm"
                                    : item.isExtraordinaria
                                      ? " apl-tag-type--especial"
                                      : ""
                                }`}
                              >
                                {LABELS_TIPO[item.tipo] || item.tipo}
                              </span>
                            ) : (
                              <span className="apl-stat-muted">—</span>
                            )}
                          </td>
                          <td>
                            <div className="apl-actions">
                              {renderAcoesItem(item)}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {itensFiltrados.length > 0 ? (
                <footer className="apl-footer">
                  <span>
                    Exibindo <strong>{itensFiltrados.length}</strong>{" "}
                    {itensFiltrados.length === 1 ? "item" : "itens"}
                    {filtro ? (
                      <>
                        {" "}
                        <span className="apl-stat-muted">
                          (de {itensAgenda.length} no total)
                        </span>
                      </>
                    ) : null}
                  </span>
                  <span>
                    {filtro
                      ? "Busca ativa"
                      : showContador
                        ? "Agenda do patrocinador"
                        : "Agenda do convidado"}
                  </span>
                </footer>
              ) : null}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdmReunioesConvidados;
